"use client";

import { cn } from "@frontend/shared/lib/utils";
import * as SelectPrimitive from "@radix-ui/react-select";
import { ChevronDown, ChevronUp } from "lucide-react";
import * as React from "react";

const KioskSelect = SelectPrimitive.Root;

const KioskSelectGroup = SelectPrimitive.Group;

const KioskSelectValue = SelectPrimitive.Value;

const KioskSelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-[44px] md:h-[50px] w-full items-center justify-between gap-3 rounded-full",
      "bg-white/80 pr-5 pl-4 py-2 text-sm md:pr-7 md:pl-6 md:py-2.5 md:text-base font-medium",
      "border border-gray-200",
      "transition-all duration-200",
      "hover:bg-white hover:border-gray-300",
      "focus:outline-none focus:ring-2 focus:ring-kioskPrimary focus:ring-offset-2",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "data-[placeholder]:text-gray-500",
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-5 w-5 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
KioskSelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const KioskSelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
KioskSelectScrollUpButton.displayName =
  SelectPrimitive.ScrollUpButton.displayName;

const KioskSelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
KioskSelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const KioskSelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-3xl",
        "bg-white/95 backdrop-blur-xl border border-gray-200",
        "shadow-[0_10px_15px_-3px_rgba(0,0,0,0.10),0_4px_6px_-4px_rgba(0,0,0,0.10)]",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
        "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className,
      )}
      position={position}
      {...props}
    >
      <KioskSelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-2",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <KioskSelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
KioskSelectContent.displayName = SelectPrimitive.Content.displayName;

const KioskSelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      "py-2 pl-2 px-6 text-sm font-semibold text-gray-500",
      className,
    )}
    {...props}
  />
));
KioskSelectLabel.displayName = SelectPrimitive.Label.displayName;

const KioskSelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-pointer select-none items-center gap-3",
      "rounded-2xl py-3 px-6 text-base outline-none",
      "transition-colors duration-150",
      "hover:bg-gray-100 focus:bg-gray-100",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      "data-[state=checked]:bg-gray-100 data-[state=checked]:text-kioskPrimary",
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
KioskSelectItem.displayName = SelectPrimitive.Item.displayName;

const KioskSelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-gray-200", className)}
    {...props}
  />
));
KioskSelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  KioskSelect,
  KioskSelectGroup,
  KioskSelectValue,
  KioskSelectTrigger,
  KioskSelectContent,
  KioskSelectLabel,
  KioskSelectItem,
  KioskSelectSeparator,
  KioskSelectScrollUpButton,
  KioskSelectScrollDownButton,
};
